import { useEffect } from 'react';
import { setAppBadge } from '../lib/appBadge';
import { isStaffRole, useUserDoc, type UserDoc } from '../lib/users';
import { useStaffWaitingOnYouCount } from '../lib/staffWaitingOnYou';
import { useFamilyMessageWaitingCount } from '../lib/familyWaitingOnYou';

/**
 * `failed` means a message check itself failed, so the count is only what
 * survived. The app icon can only show a number, so the rule that matters
 * most there is NO FALSE ZERO: a failed zero leaves the badge as it was
 * rather than clearing it, because clearing it is the phone saying "all
 * caught up" on a count nobody finished (Bobbie, 2026-09-29). A failed count
 * above zero still goes up, since it is the best floor there is.
 *
 * HOW THE HOLD ENDS, exactly, so nobody reads more into it than is there:
 *   - the must-read, mention and staff announcement listeners rebuild when
 *     the app comes back into view or the network returns
 *     (lib/listenerRevival.ts), at most once every three seconds each;
 *   - when the conversation list itself failed, useMyThreads re-asks the
 *     server every 30 seconds while the app is visible, on return to
 *     visible, and on focus; when one conversation's own listener keeps
 *     failing, it stops after three retries until a reload;
 *   - the family must-read list re-asks every 30 seconds and on focus;
 *   - the read-cursor listener (useMessageInboxStates) does NOT revive by
 *     itself; Try again on the home card reloads the page, which does.
 *
 * A FULL LIST ('capped') never reaches this component. It is not a failure:
 * the count is what was found, and the badge shows it.
 */
function BadgeValueSync({ count, failed = false }: { count: number; failed?: boolean }) {
  useEffect(() => {
    if (failed && count <= 0) return;
    setAppBadge(count);
  }, [count, failed]);
  return null;
}

function StaffAppBadgeSync({ userDoc }: { userDoc: UserDoc }) {
  const waiting = useStaffWaitingOnYouCount(userDoc);
  return <BadgeValueSync count={waiting.count} failed={Boolean(waiting.error)} />;
}

// Every row Messages shows as unread, by the rule Messages marks its rows
// with (lib/messagesUnreadCore.ts, B17), the same number as the family
// home's Waiting on you row. An Intern's Messages, and a Jr Staff roll
// parent's, also draws staff announcement rows, so the count asks the same.
function FamilyAppBadgeSync({ userDoc }: { userDoc: UserDoc }) {
  const { count } = useFamilyMessageWaitingCount(userDoc);
  return <BadgeValueSync count={count} />;
}

/**
 * Keeps the installed app icon's badge in sync with the unread notification
 * count while the app is open. Renders nothing. Background updates (app closed)
 * arrive with FCM push later. Safe no-op on browsers without the Badging API.
 */
export function AppBadgeSync() {
  const { userDoc } = useUserDoc();
  if (userDoc?.status !== 'active') return <BadgeValueSync count={0} />;
  return isStaffRole(userDoc.role)
    ? <StaffAppBadgeSync userDoc={userDoc} />
    : <FamilyAppBadgeSync userDoc={userDoc} />;
}

export default AppBadgeSync;
